import { useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { getPath, validateValues } from "./rules";
import { scrollToFirstInvalidField } from "../utils/formTouched";

/**
 * useForm with the defaults that match the app's react-reactive-form forms.
 *
 * - `rules`: form-level `{ fieldPath: rules }` map (see ./rules.js), like the
 *   validators passed to FormBuilder.group. Read through a ref, so rules that
 *   close over changing data stay current; call `form.trigger(name)` after
 *   that data changes (was `updateValueAndValidity()`).
 * - mode "all": validate on change and blur. Errors only show once a field is
 *   touched or the form was submitted (see FieldBridge).
 * - criteriaMode "all": report every failing rule, so inputs can show one
 *   message per failed validation like before.
 *
 * Extra methods on the returned form (for parts of a form that add fields at
 * runtime, like react-reactive-form's `addControl`):
 * - `form.setRules(map)` / `form.removeRules(paths)`: add or remove rules
 * - `form.isPathValid(path)`: whether every rule under `path` passes now
 * - `form.setFieldDisabled(name, bool)` / `form.isFieldDisabled(name)`: like a
 *   disabled reactive control: shown disabled and skipped by validation, while
 *   its value stays in getValues() (= getRawValue()). react-hook-form's own
 *   `disabled` would drop the value from the submitted data.
 * - `form.isPristine()`: true until the user changes a field through an input,
 *   like react-reactive-form's `form.pristine` (programmatic setValue calls,
 *   e.g. loading data or a picked logo, don't count). The first edit re-renders
 *   the form, so it can drive a Save button directly.
 * - `form.isUserChanged(name)`: the same per field (= `control.dirty`).
 * - `form.getEnabledValues()`: values without disabled fields (= `form.value`;
 *   `getValues()` is `getRawValue()`).
 */
const useAppForm = ({ rules, ...options } = {}) => {
  const rulesRef = useRef(rules || {});
  rulesRef.current = rules || {};
  const dynamicRulesRef = useRef({});
  const disabledRef = useRef({});
  const userChangedRef = useRef(false);
  const userChangedFieldsRef = useRef({});
  const [, setDisabledVersion] = useState(0);
  const [, setPristine] = useState(true);

  // A field is skipped when it, or a parent path, is disabled
  const isDisabledPath = (path) =>
    Object.entries(disabledRef.current).some(
      ([name, disabled]) => disabled && (path === name || path.startsWith(`${name}.`)),
    );

  const allRules = () => ({ ...rulesRef.current, ...dynamicRulesRef.current });

  const resolver = useMemo(
    () => (values) => {
      const errors = validateValues(allRules(), values, isDisabledPath);
      return { values: Object.keys(errors).length ? {} : values, errors };
    },
    [],
  );

  const form = useForm({
    mode: "all",
    criteriaMode: "all",
    resolver,
    ...options,
  });

  if (!form.setRules) {
    // react-hook-form's getValues() returns the *default* values until the
    // form has mounted (its own effect runs after the children's effects), so
    // values set by child effects (district_id, POC sections, ...) were
    // missing from snapshots taken on mount. Always read the live values.
    const rhfGetValues = form.getValues;
    form.getValues = (fieldNames, config) => {
      if (form.control._state.mount || config) return rhfGetValues(fieldNames, config);
      const values = { ...form.control._formValues };
      if (fieldNames === undefined) return values;
      if (typeof fieldNames === "string") return getPath(values, fieldNames);
      return fieldNames.map((name) => getPath(values, name));
    };
    form.setRules = (map) => {
      dynamicRulesRef.current = { ...dynamicRulesRef.current, ...map };
    };
    form.removeRules = (paths) => {
      const next = { ...dynamicRulesRef.current };
      paths.forEach((p) => delete next[p]);
      dynamicRulesRef.current = next;
    };
    form.isPathValid = (path) =>
      !getPath(validateValues(allRules(), form.getValues(), isDisabledPath), path);
    form.setFieldDisabled = (name, disabled = true) => {
      if (!!disabledRef.current[name] === !!disabled) return;
      disabledRef.current = { ...disabledRef.current, [name]: disabled };
      setDisabledVersion((v) => v + 1);
      form.trigger(name); // clear/restore its errors
    };
    form.isFieldDisabled = (name) => !!disabledRef.current[name];
    form.markUserChanged = (name) => {
      if (name) userChangedFieldsRef.current[name] = true;
      if (userChangedRef.current) return;
      userChangedRef.current = true;
      setPristine(false);
    };
    // Whether the user changed this field through its input (= control.dirty)
    form.isUserChanged = (name) => !!userChangedFieldsRef.current[name];
    form.getEnabledValues = () => {
      const values = snapshot(form.getValues());
      Object.entries(disabledRef.current).forEach(([name, disabled]) => {
        if (!disabled) return;
        const keys = name.split(".");
        const parent = getPath(values, keys.slice(0, -1).join("."));
        if (parent && typeof parent === "object") delete parent[keys[keys.length - 1]];
      });
      return values;
    };
    form.isPristine = () => !userChangedRef.current;
  }
  return form;
};

export default useAppForm;

// Deep copy of plain objects/arrays; other values (File, Date, ...) by reference
const snapshot = (v) => {
  if (Array.isArray(v)) return v.map(snapshot);
  if (v && Object.getPrototypeOf(v) === Object.prototype) {
    return Object.fromEntries(Object.entries(v).map(([k, x]) => [k, snapshot(x)]));
  }
  return v;
};

/**
 * Set several values at once, like react-reactive-form's `patchValue`
 * (re-validates, does not mark fields dirty or touched). Plain objects are
 * walked down to their leaves. With `onlyExisting`, keys that are not already
 * in the form are skipped, the same way `patchValue` ignored unknown controls.
 */
export const patchValues = (
  form,
  partial,
  { base = "", onlyExisting = false, validate = true } = {},
) => {
  Object.entries(partial || {}).forEach(([key, value]) => {
    const path = base ? `${base}.${key}` : key;
    const current = form.getValues(path);
    if (onlyExisting && current === undefined) return;
    if (
      value &&
      typeof value === "object" &&
      !Array.isArray(value) &&
      Object.getPrototypeOf(value) === Object.prototype &&
      current &&
      typeof current === "object"
    ) {
      patchValues(form, value, { base: path, onlyExisting, validate });
    } else {
      form.setValue(path, value, { shouldValidate: validate });
    }
  });
};

/**
 * Validate and submit, like the `formTouched()` + `form.invalid` check the
 * reactive forms used: on errors every field shows its message (the form
 * counts as submitted) and the page scrolls to the first invalid input.
 */
export const submitForm = (form, onValid, { totalTop = 135, formKey = "form" } = {}) =>
  form.handleSubmit(onValid, () => {
    setTimeout(() => scrollToFirstInvalidField({ formKey, totalTop }), 500);
  })();

/**
 * Mark a field touched (and re-validate it) without changing its value,
 * like react-reactive-form's `control.markAsTouched()`.
 */
export const markTouched = (form, name) => {
  if (form.getFieldState(name).isTouched) return;
  form.setValue(name, form.getValues(name), {
    shouldTouch: true,
    shouldValidate: true,
  });
};
